import React, { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'
import { Button } from '../Button'
import { Container } from '../Container'
import { Line } from '../Line'
import { Text } from '../Typography'

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  height: 100%;
  max-width: 800px;
  margin: 0 auto;
`

const ViewportContainer = styled.div`
  position: relative;
  width: 100%;
  height: 400px;
  background: #f3f4f6;
  overflow: hidden;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  cursor: grab;
  display: flex;
  align-items: center;
  justify-content: center;

  &:active {
    cursor: grabbing;
  }
`

const ImageLayer = styled.img`
  position: absolute;
  transform-origin: center center;
  user-select: none;
  pointer-events: none;
`

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
  border: 2px solid white;
`

const Slider = styled.input`
  width: 100%;
  margin: 10px 0;
`

const ResizeHandle = styled.div`
  position: absolute;
  width: 10px;
  height: 10px;
  background: white;
  border: 1px solid #2962ff;
  border-radius: 50%;
  pointer-events: auto;
  z-index: 11;
  
  &.se { bottom: -5px; right: -5px; cursor: nwse-resize; }
  &.sw { bottom: -5px; left: -5px; cursor: nesw-resize; }
  &.ne { top: -5px; right: -5px; cursor: nesw-resize; }
  &.nw { top: -5px; left: -5px; cursor: nwse-resize; }
`

const ResizeBorder = styled.div`
  position: absolute;
  background: transparent;
  pointer-events: auto;
  z-index: 10;
  
  &.n { top: -5px; left: 5px; right: 5px; height: 10px; cursor: ns-resize; }
  &.s { bottom: -5px; left: 5px; right: 5px; height: 10px; cursor: ns-resize; }
  &.e { right: -5px; top: 5px; bottom: 5px; width: 10px; cursor: ew-resize; }
  &.w { left: -5px; top: 5px; bottom: 5px; width: 10px; cursor: ew-resize; }
`

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
`

export const ImageEditor = ({ file, onSave, onCancel, aspectRatio = 16 / 9, aspectRatioLabel = '16:9' }) => {
  const [imageSrc, setImageSrc] = useState(null)
  const [scale, setScale] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [dragging, setDragging] = useState(false)
  const [startPos, setStartPos] = useState({ x: 0, y: 0 })
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 })
  const [customCrop, setCustomCrop] = useState(false)
  const [cropRect, setCropRect] = useState({ x: 0, y: 0, width: 0, height: 0 })
  const [isResizingCrop, setIsResizingCrop] = useState(false)
  const [resizeHandle, setResizeHandle] = useState(null)
  
  const viewportRef = useRef(null)
  const imageRef = useRef(null)

  // Load image from file
  useEffect(() => {
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => setImageSrc(e.target.result)
      reader.readAsDataURL(file)
    }
  }, [file])

  // Calculate viewport size based on aspect ratio
  useEffect(() => {
    if (viewportRef.current) {
      const { width: containerWidth, height: containerHeight } = viewportRef.current.getBoundingClientRect()
      
      let width = containerWidth - 40 // Padding
      let height = width / aspectRatio

      if (height > containerHeight - 40) {
        height = containerHeight - 40
        width = height * aspectRatio
      }

      const x = (containerWidth - width) / 2
      const y = (containerHeight - height) / 2

      setCropRect({ x, y, width, height })
    }
  }, [aspectRatio])

  const handleImageLoad = (e) => {
    setImageSize({ width: e.target.naturalWidth, height: e.target.naturalHeight })
    // Initial fit
    const scaleX = cropRect.width / e.target.naturalWidth
    const scaleY = cropRect.height / e.target.naturalHeight
    const initialScale = Math.max(scaleX, scaleY) || 1
    setScale(initialScale)
  }

  const handleMouseDown = (e) => {
    // If resizing crop rect, handle separately
    if (isResizingCrop) return

    setDragging(true)
    setStartPos({ x: e.clientX - position.x, y: e.clientY - position.y })
  }

  const handleMouseMove = (e) => {
    if (isResizingCrop && customCrop && resizeHandle && viewportRef.current) {
      const { width: containerWidth, height: containerHeight } = viewportRef.current.getBoundingClientRect()
      const rect = viewportRef.current.getBoundingClientRect()
      const mouseX = e.clientX - rect.left
      const mouseY = e.clientY - rect.top

      let { x, y, width, height } = cropRect
      const minSize = 50

      // Handle horizontal resizing
      if (resizeHandle.includes('e')) {
        width = Math.min(Math.max(mouseX - x, minSize), containerWidth - x)
      } else if (resizeHandle.includes('w')) {
        const newX = Math.max(0, Math.min(mouseX, x + width - minSize))
        width = width + (x - newX)
        x = newX
      }

      // Handle vertical resizing
      if (resizeHandle.includes('s')) {
        height = Math.min(Math.max(mouseY - y, minSize), containerHeight - y)
      } else if (resizeHandle.includes('n')) {
        const newY = Math.max(0, Math.min(mouseY, y + height - minSize))
        height = height + (y - newY)
        y = newY
      }

      setCropRect({ x, y, width, height })
      return
    }

    if (dragging) {
      setPosition({
        x: e.clientX - startPos.x,
        y: e.clientY - startPos.y
      })
    }
  }

  const handleMouseUp = () => {
    setDragging(false)
    setIsResizingCrop(false)
    setResizeHandle(null)
  }

  const handleResizeMouseDown = (e, handle) => {
    e.stopPropagation()
    setIsResizingCrop(true)
    setResizeHandle(handle)
  }

  const handleSave = async () => {
    if (!imageRef.current || !viewportRef.current) return

    const canvas = document.createElement('canvas')
    canvas.width = cropRect.width
    canvas.height = cropRect.height
    const ctx = canvas.getContext('2d')

    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    
    const { width: containerWidth, height: containerHeight } = viewportRef.current.getBoundingClientRect()

    ctx.save()
    // Translate so that the crop area's top-left becomes (0,0) relative to the image position
    const centerX = (containerWidth / 2) + position.x - cropRect.x
    const centerY = (containerHeight / 2) + position.y - cropRect.y
    
    ctx.translate(centerX, centerY)
    ctx.scale(scale, scale)
    ctx.drawImage(
      imageRef.current, 
      -imageSize.width / 2, 
      -imageSize.height / 2
    )
    ctx.restore()

    canvas.toBlob((blob) => {
      if (blob) {
        const newFile = new File([blob], file.name, { type: 'image/png' })
        onSave(newFile)
      }
    }, 'image/png')
  }

  return (
    <EditorContainer>
      <Container direction="vertical" gap={8}>
        <Text weight="600" size="16px">Modifica Immagine</Text>
        <Text size="14px" color="#6b7280">
          Trascina per posizionare e usa lo slider per zoomare. 
          Formato consigliato: <span style={{ fontWeight: 600, color: '#2962ff' }}>{aspectRatioLabel}</span>
        </Text>
      </Container>

      <ViewportContainer 
        ref={viewportRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {imageSrc && (
          <ImageLayer
            ref={imageRef}
            src={imageSrc}
            onLoad={handleImageLoad}
            style={{
              transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`
            }}
            draggable={false}
          />
        )}
        <Overlay style={{ 
          width: cropRect.width, 
          height: cropRect.height,
          left: cropRect.x,
          top: cropRect.y,
          pointerEvents: 'none'
        }}>
          {customCrop && (
            <>
              {/* Corners */}
              <ResizeHandle className="nw" onMouseDown={(e) => handleResizeMouseDown(e, 'nw')} />
              <ResizeHandle className="ne" onMouseDown={(e) => handleResizeMouseDown(e, 'ne')} />
              <ResizeHandle className="sw" onMouseDown={(e) => handleResizeMouseDown(e, 'sw')} />
              <ResizeHandle className="se" onMouseDown={(e) => handleResizeMouseDown(e, 'se')} />
              
              {/* Borders */}
              <ResizeBorder className="n" onMouseDown={(e) => handleResizeMouseDown(e, 'n')} />
              <ResizeBorder className="s" onMouseDown={(e) => handleResizeMouseDown(e, 's')} />
              <ResizeBorder className="e" onMouseDown={(e) => handleResizeMouseDown(e, 'e')} />
              <ResizeBorder className="w" onMouseDown={(e) => handleResizeMouseDown(e, 'w')} />
            </>
          )}
        </Overlay>
      </ViewportContainer>

      <Container direction="vertical" gap={8}>
        <Line justify="space-between" alignItems="center">
          <Text size="13px" weight="500">Zoom: {Math.round(scale * 100)}%</Text>
          <CheckboxLabel>
            <input 
              type="checkbox" 
              checked={customCrop} 
              onChange={(e) => setCustomCrop(e.target.checked)} 
            />
            <Text size="13px">Ritaglio Libero</Text>
          </CheckboxLabel>
        </Line>
        <Slider 
          type="range" 
          min="0.1" 
          max="3" 
          step="0.01" 
          value={scale} 
          onChange={(e) => setScale(parseFloat(e.target.value))} 
        />
      </Container>

      <Line justify="flex-end" gap={12}>
        <Button variant="ghost" onClick={onCancel}>Annulla</Button>
        <Button onClick={handleSave}>Salva e Carica</Button>
      </Line>
    </EditorContainer>
  )
}
